import { forwardRef, useCallback, useMemo, useState } from "react"
import { Slide, SlideProps, SxProps, useTheme } from "@mui/material"
import { deepmerge } from "@mui/utils"
import { Id } from "@turf/helpers"

import { useTranslation } from "providers/i18n"
import { PromotionData } from "providers/venue/types"

import { BaseModal } from "components/modal/BaseModal"
import { PromotionDetailCard } from "components/domains/promotions/OccupantPromotionModal/PromotionDetailCard"
import { PromotionsCarousel } from "components/domains/promotions/OccupantPromotionModal/PromotionsCarousel"
import {
  DetailPage,
  IDetailPageProps,
} from "components/domains/features/DetailPage"

interface IPromotionDetailModalState {
  isOpen: boolean
  promotionId: Id
}

const DEFAULT_PROMOTION_DETAIL_MODAL_STATE: IPromotionDetailModalState = {
  isOpen: false,
  promotionId: "",
}

interface IOccupantPromotionModalProps {
  sx?: SxProps
  occupantName: string
  promotions: PromotionData[]
  open: boolean
  onNavigate: () => void
  onClose: () => void
  DetailPageComponent?: React.FC<IDetailPageProps>
}

const ModalTransition = forwardRef<HTMLElement, SlideProps>((props, ref) => {
  return <Slide direction="up" ref={ref} {...props} />
})

export const OccupantPromotionModal = ({
  sx = {},
  occupantName,
  promotions,
  open,
  onNavigate,
  onClose,
  DetailPageComponent = DetailPage,
  ...props
}: IOccupantPromotionModalProps) => {
  const [promotionDetailCard, setPromotionDetailCard] = useState(
    DEFAULT_PROMOTION_DETAIL_MODAL_STATE
  )

  const { t } = useTranslation()

  const theme = useTheme()

  const { styleOverrides } = theme.components[`OccupantPromotionModal`] || {}
  const {
    root,
    closeIconButton,
    promotionDetailCardStyle,
    promotionsCarouselStyle,
  } = styleOverrides

  const headerText = useMemo(
    () => `${occupantName} ${t("Promotions")}`,
    [occupantName, t]
  )

  const handleClickCard = useCallback((id: Id) => {
    setPromotionDetailCard({ isOpen: true, promotionId: id })
  }, [])

  const handleClickBackPromotionDetailCard = useCallback(() => {
    setPromotionDetailCard(DEFAULT_PROMOTION_DETAIL_MODAL_STATE)
  }, [])

  const handleCloseModal = useCallback(() => {
    setPromotionDetailCard(DEFAULT_PROMOTION_DETAIL_MODAL_STATE)
    onClose()
  }, [onClose])

  return (
    <BaseModal
      sx={deepmerge(root, sx)}
      open={open}
      onClose={handleCloseModal}
      closeIconButtonProps={{
        sx: closeIconButton,
      }}
      TransitionComponent={ModalTransition}
      className={promotionDetailCard.isOpen ? "PromotionDetailCard-open" : ""}
      {...props}
    >
      {promotionDetailCard.isOpen ? (
        <PromotionDetailCard
          sx={promotionDetailCardStyle}
          promotionId={promotionDetailCard.promotionId}
          onNavigate={onNavigate}
          onBack={handleClickBackPromotionDetailCard}
          DetailPageComponent={DetailPageComponent}
        />
      ) : (
        <PromotionsCarousel
          sx={promotionsCarouselStyle}
          headerText={headerText}
          promotions={promotions}
          onClickCard={handleClickCard}
        />
      )}
    </BaseModal>
  )
}
